/**
 * MUTAGIM.SHOP — Phase 17: A/B Testing
 * Page de gestion des tests A/B
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Target, Plus, ChevronLeft, Loader2, Play, Pause, Trophy,
  CheckCircle, XCircle, BarChart3, Eye, Trash2, ArrowRight
} from 'lucide-react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
}

export function AdminABTestsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [tests, setTests] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  
  // Create form state
  const [formData, setFormData] = useState({
    name: '',
    product_id: '',
    variable: 'price',
    variant_a_value: '',
    variant_b_value: '',
    duration_days: 7,
    min_impressions: 500,
  });
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = getToken();
    
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      // Fetch A/B tests
      const testsRes = await fetch(`${API_URL}/api/analytics/ab-tests`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (testsRes.ok) {
        const data = await testsRes.json();
        setTests(data.ab_tests || []);
      }

      // Fetch products
      const productsRes = await fetch(`${API_URL}/api/products?limit=100`);
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createTest = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.product_id || !formData.variant_a_value || !formData.variant_b_value) {
      toast.error('Remplissez tous les champs');
      return;
    }

    setCreating(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/analytics/ab-tests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success('Test A/B créé !');
        setShowCreateDialog(false);
        setFormData({
          name: '',
          product_id: '',
          variable: 'price',
          variant_a_value: '',
          variant_b_value: '',
          duration_days: 7,
          min_impressions: 500,
        });
        fetchData();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const updateTestStatus = async (testId, status) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/analytics/ab-tests/${testId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (res.ok) {
        toast.success(`Test ${status === 'paused' ? 'mis en pause' : 'relancé'}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const declareWinner = async (testId, winner) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/analytics/ab-tests/${testId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ winner }),
      });

      if (res.ok) {
        toast.success(`Variante ${winner.toUpperCase()} déclarée gagnante !`);
        fetchData();
        setSelectedTest(null);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const applyWinner = async (testId) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/analytics/ab-tests/${testId}/apply-winner`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success('Gagnant appliqué au produit !');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      running: { color: 'bg-green-500/20 text-green-400', label: 'En cours' },
      paused: { color: 'bg-yellow-500/20 text-yellow-400', label: 'En pause' },
      completed: { color: 'bg-blue-500/20 text-blue-400', label: 'Terminé' },
    };
    const config = configs[status] || configs.running;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getVariableLabel = (variable) => {
    const labels = { price: 'Prix', visual: 'Visuel', copy: 'Copy' };
    return labels[variable] || variable;
  };

  return (
    <AdminPageWrapper title="A/B Testing" subtitle="Optimisez vos conversions">
      <div data-testid="admin-ab-tests-page">
        {/* Header avec bouton créer */}
        <div className="flex items-center justify-between mb-6">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700" data-testid="create-ab-test">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Test
              </Button>
            </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-white">Créer un test A/B</DialogTitle>
                </DialogHeader>
                <form onSubmit={createTest} className="space-y-4">
                  <div>
                    <Label>Nom du test</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Test prix Jordan 1"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  
                  <div>
                    <Label>Produit</Label>
                    <Select 
                      value={formData.product_id} 
                      onValueChange={(v) => setFormData({ ...formData, product_id: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Sélectionner un produit" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name?.fr || p.slug}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Variable à tester</Label>
                    <Select 
                      value={formData.variable} 
                      onValueChange={(v) => setFormData({ ...formData, variable: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="price">Prix</SelectItem>
                        <SelectItem value="visual">Visuel</SelectItem>
                        <SelectItem value="copy">Copy/Description</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Variante A</Label>
                      <Input
                        value={formData.variant_a_value}
                        onChange={(e) => setFormData({ ...formData, variant_a_value: e.target.value })}
                        placeholder={formData.variable === 'price' ? '199' : 'Valeur A'}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <Label>Variante B</Label>
                      <Input
                        value={formData.variant_b_value}
                        onChange={(e) => setFormData({ ...formData, variant_b_value: e.target.value })}
                        placeholder={formData.variable === 'price' ? '179' : 'Valeur B'}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Durée (jours)</Label>
                      <Input
                        type="number"
                        value={formData.duration_days}
                        onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                        min={1}
                        max={30}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <Label>Min. impressions</Label>
                      <Input
                        type="number"
                        value={formData.min_impressions}
                        onChange={(e) => setFormData({ ...formData, min_impressions: parseInt(e.target.value) })}
                        min={100}
                        max={10000}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>

                  <Button type="submit" disabled={creating} className="w-full bg-purple-600 hover:bg-purple-700">
                    {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Créer le test
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
            </div>
          ) : tests.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-12 text-center">
                <Target className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Aucun test A/B</h3>
                <p className="text-white mb-6">
                  Créez votre premier test pour optimiser vos conversions
                </p>
                <Button onClick={() => setShowCreateDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un test
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {tests.map((test) => (
                <Card key={test.id} className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-xl font-semibold text-white">{test.name}</h3>
                          {getStatusBadge(test.status)}
                          <Badge variant="outline">{getVariableLabel(test.variable)}</Badge>
                        </div>
                        <p className="text-sm text-white mt-1">
                          {test.product?.name?.fr || 'Produit'} • 
                          Créé le {new Date(test.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {test.status === 'running' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => updateTestStatus(test.id, 'paused')}
                          >
                            <Pause className="w-4 h-4 mr-1" />
                            Pause
                          </Button>
                        )}
                        {test.status === 'paused' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => updateTestStatus(test.id, 'running')}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Reprendre
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedTest(test)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Détails
                        </Button>
                      </div>
                    </div>

                    {/* Variants Comparison */}
                    <div className="grid md:grid-cols-2 gap-4 mt-6">
                      {/* Variant A */}
                      <div className={cn(
                        "p-4 rounded-lg border-2 transition-all",
                        test.winner === 'a' ? "border-green-500 bg-green-500/10" : "border-zinc-700"
                      )}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">A</span>
                            <span className="text-white font-medium">Variante A</span>
                          </div>
                          {test.winner === 'a' && (
                            <Badge className="bg-green-500/20 text-green-400">
                              <Trophy className="w-3 h-3 mr-1" />
                              Gagnant
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-white mb-3">
                          Valeur: <span className="text-white">{test.variant_a?.value || 'N/A'}</span>
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-gray-200">Impressions</p>
                            <p className="text-white font-bold">{test.variant_a?.impressions || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-200">Clics</p>
                            <p className="text-white font-bold">{test.variant_a?.clicks || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-200">Ajouts panier</p>
                            <p className="text-white font-bold">{test.variant_a?.add_to_cart || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-200">Achats</p>
                            <p className="text-green-400 font-bold">{test.variant_a?.purchases || 0}</p>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-zinc-700">
                          <p className="text-gray-200 text-sm">Taux de conversion</p>
                          <p className="text-2xl font-bold text-blue-400">
                            {test.variant_a?.conversion_rate || 0}%
                          </p>
                        </div>
                      </div>

                      {/* Variant B */}
                      <div className={cn(
                        "p-4 rounded-lg border-2 transition-all",
                        test.winner === 'b' ? "border-green-500 bg-green-500/10" : "border-zinc-700"
                      )}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold">B</span>
                            <span className="text-white font-medium">Variante B</span>
                          </div>
                          {test.winner === 'b' && (
                            <Badge className="bg-green-500/20 text-green-400">
                              <Trophy className="w-3 h-3 mr-1" />
                              Gagnant
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-white mb-3">
                          Valeur: <span className="text-white">{test.variant_b?.value || 'N/A'}</span>
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-gray-200">Impressions</p>
                            <p className="text-white font-bold">{test.variant_b?.impressions || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-200">Clics</p>
                            <p className="text-white font-bold">{test.variant_b?.clicks || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-200">Ajouts panier</p>
                            <p className="text-white font-bold">{test.variant_b?.add_to_cart || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-200">Achats</p>
                            <p className="text-green-400 font-bold">{test.variant_b?.purchases || 0}</p>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-zinc-700">
                          <p className="text-gray-200 text-sm">Taux de conversion</p>
                          <p className="text-2xl font-bold text-purple-400">
                            {test.variant_b?.conversion_rate || 0}%
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Progress & Actions */}
                    {test.status === 'running' && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-white">
                            Progression: {test.variant_a?.impressions + test.variant_b?.impressions || 0} / {test.min_impressions || 500} impressions
                          </span>
                          {test.suggested_winner && (
                            <Badge className="bg-yellow-500/20 text-yellow-400">
                              Gagnant suggéré: {test.suggested_winner.toUpperCase()}
                            </Badge>
                          )}
                        </div>
                        <Progress 
                          value={Math.min(((test.variant_a?.impressions + test.variant_b?.impressions || 0) / (test.min_impressions || 500)) * 100, 100)} 
                          className="h-2"
                        />
                      </div>
                    )}

                    {/* Winner Actions */}
                    {test.status !== 'completed' && (
                      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-zinc-800">
                        <span className="text-white text-sm">Déclarer le gagnant :</span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => declareWinner(test.id, 'a')}
                          className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Variante A
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => declareWinner(test.id, 'b')}
                          className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Variante B
                        </Button>
                      </div>
                    )}

                    {/* Apply Winner */}
                    {test.status === 'completed' && test.winner && !test.winner_applied && (
                      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-zinc-800">
                        <Button 
                          onClick={() => applyWinner(test.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <ArrowRight className="w-4 h-4 mr-2" />
                          Appliquer le gagnant au produit
                        </Button>
                        <span className="text-white text-sm">
                          La variante {test.winner.toUpperCase()} sera appliquée
                        </span>
                      </div>
                    )}

                    {test.winner_applied && (
                      <div className="mt-6 pt-4 border-t border-zinc-800">
                        <Badge className="bg-green-500/20 text-green-400">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Gagnant appliqué le {new Date(test.applied_at).toLocaleDateString('fr-FR')}
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminPageWrapper>
  );
}

export default AdminABTestsPage;
